/**
 * PendingMemberInvitations
 *
 * Fires a transient toast (once per session) when the current user has pending
 * invitations to join another owner's property, pointing them to the permanent
 * "Invitations" section on their Profile to accept. Renders no persistent UI —
 * it's an alert that disappears, not a stuck banner.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { homeownerMembersService } from '@/services/supabase/homeownerMembers.service';

const SESSION_FLAG = 'zentrian_pending_invite_toast_shown';

export const PendingMemberInvitations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firedRef = useRef(false);

  const { data: invitations = [] } = useQuery({
    queryKey: ['myPendingInvitations'],
    queryFn: () => homeownerMembersService.getMyPendingInvitations(),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (invitations.length === 0 || firedRef.current) return;
    // Only once per browser session, so it doesn't nag on every navigation.
    if (sessionStorage.getItem(SESSION_FLAG)) return;

    firedRef.current = true;
    sessionStorage.setItem(SESSION_FLAG, '1');

    toast(
      t('features.homeowner.members.pendingInvites.toastTitle', 'You have a pending invitation'),
      {
        description: t(
          'features.homeowner.members.pendingInvites.toastDesc',
          'Accept it in your Profile → Invitations.',
        ),
        duration: 8000,
        action: {
          label: t('common.view', 'View'),
          onClick: () => navigate('/home-owner/profile'),
        },
      },
    );
  }, [invitations, t, navigate]);

  return null;
};
